/**
 * 知识图鉴弹窗
 *
 * 它是「技能节点」与「知识卡」之间的那扇门：解锁节点时弹出，
 * 也用于错题复习与图鉴翻阅。同一份卡在不同入口复用同一套渲染。
 *
 * 排版细节：正文里的 `**重点**` 会转成加粗 —— 我们的卡库就是这么写的，
 * 这里做一层最小 Markdown（只支持加粗与换行），不引入 Markdown 依赖。
 */

import type { KnowledgeCard } from "@chuandeng/shared";
import { esc } from "./dom.js";
import { type ModalHandle, openModal } from "./modal.js";

const SUBJECT_TEXT: Record<KnowledgeCard["subject"], string> = {
  biology: "生物与卫生健康",
  geography: "地理",
  history: "历史",
  math: "数学",
};

const GRADE_TEXT: Record<KnowledgeCard["grade"], string> = {
  "primary-high": "小学高年级",
  junior: "初中",
};

const TORCH_TEXT: Record<KnowledgeCard["torch"], string> = {
  craft: "工艺火种",
  life: "生命火种",
  math: "数理火种",
  culture: "文脉火种",
};

/** 最小 Markdown：**加粗** + 段落换行 */
export function renderCardBody(body: string): string {
  return body
    .split(/\n{2,}/)
    .map(
      (paragraph) =>
        `<p>${esc(paragraph.trim()).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br />")}</p>`,
    )
    .join("");
}

export interface CodexOptions {
  /** 从哪来（技能树 / 结算 / 错题复习），只影响 eyebrow 文案 */
  origin?: string;
  /** 是否展示卡片自带的小测并允许作答（复习场景需要） */
  interactiveQuiz?: boolean;
  onAnswered?(correct: boolean): void;
  onRead?(cardId: string): void;
}

export function openCodex(card: KnowledgeCard, options: CodexOptions = {}): ModalHandle {
  const isDraft = !(card.status === "approved" && card.reviewedBy !== null);

  const badges = [
    `<span class="badge badge--info">${esc(SUBJECT_TEXT[card.subject])}</span>`,
    `<span class="badge badge--info">${esc(GRADE_TEXT[card.grade])}</span>`,
    `<span class="badge badge--info">${esc(TORCH_TEXT[card.torch])}</span>`,
    ...card.curriculum.map((tag) => `<span class="badge badge--ok">${esc(tag)}</span>`),
    isDraft
      ? `<span class="badge badge--draft">待人工审核</span>`
      : `<span class="badge badge--ok">已审核 ${esc(card.reviewedBy ?? "")}</span>`,
  ].join("");

  const quizHtml = options.interactiveQuiz
    ? `
      <h3 class="panel-title" style="margin-top:1.1rem">小测</h3>
      <p class="quiz-question">${esc(card.quiz.question)}</p>
      <ul class="quiz-options" data-quiz>
        ${card.quiz.options
          .map(
            (option, index) => `<li>
              <button class="quiz-option" type="button" data-index="${index}">
                <span class="key">${String.fromCharCode(65 + index)}</span>
                <span>${esc(option)}</span>
              </button>
            </li>`,
          )
          .join("")}
      </ul>
      <div data-quiz-feedback hidden></div>`
    : `
      <details style="margin-top:1rem">
        <summary style="cursor:pointer;font-weight:700">看一眼小测</summary>
        <p class="quiz-question" style="margin-top:.6rem">${esc(card.quiz.question)}</p>
        <ol style="font-size:.86rem;color:var(--ink-soft)">
          ${card.quiz.options.map((option) => `<li>${esc(option)}</li>`).join("")}
        </ol>
        <p style="font-size:.86rem;color:var(--ink-soft)">
          <strong>答案：</strong>${esc(card.quiz.options[card.quiz.answerIndex] ?? "")}
        </p>
        <p style="font-size:.86rem;color:var(--ink-soft)">${esc(card.quiz.explanation)}</p>
      </details>`;

  const bodyHtml = `
    <p style="margin:0 0 .4rem;color:var(--ink-soft);font-size:.9rem">${esc(card.summary)}</p>
    <div class="codex-meta">${badges}</div>
    <div class="codex-body">${renderCardBody(card.body)}</div>
    ${quizHtml}
    <p style="margin:.9rem 0 0;font-size:.76rem;color:var(--ink-soft)">
      复习节奏：${card.reviewStages.map((d) => `${d} 天`).join(" → ")}
    </p>`;

  const handle = openModal({
    eyebrow: options.origin ?? "知识图鉴",
    title: card.title,
    bodyHtml,
    actions: [{ label: "收起", value: "close" }],
    onAction: () => handle.close(),
    onClose: () => options.onRead?.(card.id),
  });

  if (options.interactiveQuiz) {
    const list = handle.element.querySelector<HTMLElement>("[data-quiz]");
    const feedback = handle.element.querySelector<HTMLElement>("[data-quiz-feedback]");
    list?.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLElement>("[data-index]");
      if (!button) return;
      const index = Number.parseInt(button.dataset["index"] ?? "", 10);
      if (!Number.isFinite(index)) return;

      const correct = index === card.quiz.answerIndex;
      for (const node of list.querySelectorAll<HTMLButtonElement>(".quiz-option")) {
        node.disabled = true;
        const nodeIndex = Number.parseInt(node.dataset["index"] ?? "", 10);
        if (nodeIndex === card.quiz.answerIndex) node.classList.add("is-correct");
        else if (nodeIndex === index) node.classList.add("is-wrong");
      }
      if (feedback) {
        feedback.hidden = false;
        feedback.innerHTML = `
          <div class="quiz-explain">
            <strong>${correct ? "✓ 答对了：这笔经验记住了。" : "✗ 这次没答对，不要紧 —— 它已经进入你的错题集，过几天我们再来看它。"}</strong><br />
            ${esc(card.quiz.explanation)}
          </div>`;
      }
      options.onAnswered?.(correct);
    });
  }

  return handle;
}
